import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { Shop, getShopList, ShopListRequest } from '../../utils/shop';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getBrandList, BrandNode } from '../../utils/brand';
import { 
  Home,
  Search,
  ChevronRight,
  ChevronDown,
  Store,
  Folder,
  FolderOpen,
  Edit,
  Trash2,
  Eye,
  Download,
  Plus,
  MapPin,
  Clock,
  Phone,
  ExternalLink
} from 'lucide-react';

// 브랜드 트리 노드 인터페이스
interface BrandTreeNode {
  code: string;
  name: string;
  type: 'brand' | 'group';
  depth: number;
  shopCount?: number; // 직접 소속 매장 수
  totalShopCount?: number; // 하위 브랜드 포함 전체 매장 수
  children?: BrandTreeNode[];
}

const BrandList: React.FC = () => {
  const navigate = useNavigate();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('brand-list');

  // Tree state
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<BrandTreeNode | null>(null);
  const [treeData, setTreeData] = useState<BrandTreeNode[]>([]);

  // Shop list state
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);
  const [shopListError, setShopListError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Brand list state
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [brandListError, setBrandListError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // BrandNode를 BrandTreeNode로 변환하는 함수 (하위 브랜드 매장 수 합산)
  const convertBrandNodeToTreeNode = (brandNode: BrandNode): BrandTreeNode => {
    // 하위 브랜드들을 먼저 변환
    const children = brandNode.subBrands?.map(subBrand => 
      convertBrandNodeToTreeNode(subBrand)
    );

    // 하위 브랜드의 totalShopCount 합산
    const subBrandShopCount = children?.reduce((sum, child) => 
      sum + (child.totalShopCount || 0), 0
    ) || 0;

    // 직접 소속 매장 수
    const directShopCount = brandNode.shopCount || 0;

    // 하위 브랜드 포함 전체 매장 수
    const totalShopCount = directShopCount + subBrandShopCount;

    const node: BrandTreeNode = {
      code: brandNode.code,
      name: brandNode.name,
      type: brandNode.depth === 0 ? 'brand' : 'group',
      depth: brandNode.depth,
      shopCount: directShopCount, // 직접 소속 매장 수
      totalShopCount: totalShopCount, // 하위 브랜드 포함 전체 매장 수
      children
    };
    return node;
  };

  // 브랜드 트리 데이터 로드
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }

      try {
        setIsLoadingBrands(true);
        setBrandListError(null);

        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        const response = await getBrandList(projectCode, true); // includeShopCount=true
        const treeNodes = response.brands.map(brand => convertBrandNodeToTreeNode(brand));
        setTreeData(treeNodes);
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setBrandListError(err instanceof Error ? err.message : '브랜드 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoadingBrands(false);
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // 선택된 브랜드의 매장 목록 조회
  useEffect(() => {
    const fetchShopsForBrand = async () => {
      if (!selectedNode || !projectDetail) {
        setShops([]);
        setShopListError(null);
        return;
      }

      try {
        setIsLoadingShops(true);
        setShopListError(null);

        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }

        // 브랜드 선택 시 하위 브랜드의 매장도 포함하여 조회
        const request: ShopListRequest = {
          brandCode: selectedNode.code,
          includeSubBrands: true
          // 페이징 없이 전체 조회
        };

        const response = await getShopList(projectCode, request);
        setShops(response.shops || []);
      } catch (err) {
        console.error('매장 목록 조회 오류:', err);
        setShopListError(err instanceof Error ? err.message : '매장 목록을 불러오는 중 오류가 발생했습니다.');
        setShops([]);
      } finally {
        setIsLoadingShops(false);
      }
    };

    fetchShopsForBrand();
  }, [selectedNode, projectDetail]);

  // 트리 노드 토글
  const toggleNode = (nodeCode: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeCode)) {
      newExpanded.delete(nodeCode);
    } else {
      newExpanded.add(nodeCode);
    }
    setExpandedNodes(newExpanded);
  };

  // 트리 노드 선택
  const handleNodeSelect = (node: BrandTreeNode) => {
    setSelectedNode(node);
    setSearchTerm(''); // 브랜드 선택 시 검색어 초기화
  };

  // 트리 노드 렌더링
  const renderTreeNode = (node: BrandTreeNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(node.code);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selectedNode?.code === node.code;

    return (
      <div key={node.code}>
        <div
          className={`flex items-center py-2 px-2 rounded-lg cursor-pointer transition-colors ${
            isSelected
              ? 'bg-blue-100 text-blue-900'
              : 'hover:bg-gray-100 text-gray-700'
          }`}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
          onClick={() => handleNodeSelect(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.code);
              }}
              className="mr-2 p-0.5 hover:bg-gray-200 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-5 mr-2" />
          )}
          
          {node.type === 'brand' && (
            isExpanded ? (
              <FolderOpen className="w-4 h-4 mr-2 text-blue-600" />
            ) : (
              <Folder className="w-4 h-4 mr-2 text-blue-600" />
            )
          )}
          {node.type === 'group' && (
            <Folder className="w-4 h-4 mr-2 text-gray-500" />
          )}
          
          <span className="flex-1 text-sm font-medium">{node.name}</span>
          {node.totalShopCount !== undefined && (
            <span className="text-xs text-gray-500 ml-2">({node.totalShopCount})</span>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children!.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return ShopFormUtils.formatDate(dateString);
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return dateString.split(' ')[0];
    }
  };

  // 상태 뱃지
  const getStatusBadge = (status: string) => {
    const badgeInfo = ShopFormUtils.getStatusBadge(status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeInfo.color}`}>
        <span className="w-1.5 h-1.5 mr-1 bg-current rounded-full"></span>
        {badgeInfo.text}
      </span>
    );
  };

  // 필터링된 매장 목록 (검색어로 필터링)
  const filteredShops = shops.filter(shop => {
    if (!searchTerm.trim()) {
      return true;
    }
    const searchLower = searchTerm.toLowerCase();
    return (
      shop.name.toLowerCase().includes(searchLower) ||
      shop.outletCode.toLowerCase().includes(searchLower) ||
      (shop.brandName && shop.brandName.toLowerCase().includes(searchLower))
    );
  });

  // 프로젝트 정보가 없을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="매장 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* Brand List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="flex flex-col lg:flex-row gap-4 h-full">
            {/* Top Section (Mobile) / Left Section (Desktop) - Brand Tree */}
            <div className="w-full lg:w-80 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-64 lg:h-auto">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">브랜드 목록</h2>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {isLoadingBrands ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : brandListError ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <p className="text-red-600 text-sm mb-4 text-center">{brandListError}</p>
                    <button
                      onClick={() => {
                        setBrandListError(null);
                        const projectCode = projectDetail?.code || getProjectCode();
                        if (projectCode) {
                          getBrandList(projectCode, true).then(response => {
                            const treeNodes = response.brands.map(brand => convertBrandNodeToTreeNode(brand));
                            setTreeData(treeNodes);
                          }).catch(err => {
                            setBrandListError(err instanceof Error ? err.message : '브랜드 목록을 불러오는 중 오류가 발생했습니다.');
                          });
                        }
                      }}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : treeData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Folder className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">브랜드가 없습니다.</p>
                  </div>
                ) : (
                  <div>
                    {treeData.map(node => renderTreeNode(node))}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Section (Mobile) / Right Section (Desktop) - Shop List */}
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {selectedNode ? `${selectedNode.name} - 매장 목록` : '매장 목록'}
                  </h2>
                  {selectedNode && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center text-sm"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        엑셀
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Search */}
              {selectedNode && (
                <div className="p-4 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="매장명, 코드, 브랜드명으로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Shop List */}
              <div className="flex-1 overflow-y-auto">
                {!selectedNode ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Store className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-gray-500 text-center px-4">
                      <span className="lg:hidden">위에서</span>
                      <span className="hidden lg:inline">왼쪽에서</span> 브랜드를 선택하세요
                    </p>
                  </div>
                ) : isLoadingShops ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : shopListError ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4">
                    <div className="text-red-500 mb-4">
                      <Home className="w-12 h-12 mx-auto" />
                    </div>
                    <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{shopListError}</p>
                    <button
                      onClick={() => {
                        setShopListError(null);
                        const projectCode = projectDetail?.code || getProjectCode();
                        if (projectCode && selectedNode) {
                          const request: ShopListRequest = {
                            brandCode: selectedNode.code,
                            includeSubBrands: true
                          };
                          getShopList(projectCode, request).then(response => {
                            setShops(response.shops || []);
                          }).catch(err => {
                            setShopListError(err instanceof Error ? err.message : '매장 목록을 불러오는 중 오류가 발생했습니다.');
                          });
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                ) : filteredShops.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Store className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      {searchTerm ? '검색 결과가 없습니다.' : '매장이 없습니다.'}
                    </p>
                  </div>
                ) : (
                  <div className="p-4">
                    <div className="mb-4 text-sm text-gray-600">
                      총 <span className="font-medium text-gray-900">{filteredShops.length}</span>개 매장
                      {searchTerm && (
                        <span className="ml-2 text-gray-500">(검색어: "{searchTerm}")</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {filteredShops.map((shop) => (
                        <div
                          key={shop.code}
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-gray-900">
                                  {shop.brandName ? `[${shop.brandName}] ${shop.name}` : shop.name}
                                </h3>
                                {getStatusBadge(shop.status)}
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex items-center">
                                  <span className="w-20 text-gray-500">코드:</span>
                                  <span>{shop.outletCode}</span>
                                </div>
                                {(shop.addressRoad || shop.addressDetail) && (
                                  <div className="flex items-start">
                                    <MapPin className="w-4 h-4 text-gray-400 mr-1 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600">
                                      {shop.addressRoad || ''} {shop.addressDetail || ''}
                                    </span>
                                  </div>
                                )}
                                {shop.phoneNumber && (
                                  <div className="flex items-center">
                                    <Phone className="w-4 h-4 text-gray-400 mr-1" />
                                    <span>{shop.phoneNumber}</span>
                                  </div>
                                )}
                                {shop.createdAt && (
                                  <div className="flex items-center">
                                    <Clock className="w-4 h-4 text-gray-400 mr-1" />
                                    <span>생성일: {formatDate(shop.createdAt)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {shop.homepage && (
                                <button
                                  className="text-gray-400 hover:text-blue-600"
                                  title="홈페이지 보기"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                className="text-gray-400 hover:text-gray-600"
                                title="상세 보기"
                                onClick={() => alert('상세 보기 기능은 준비중입니다.')}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                className="text-gray-400 hover:text-blue-600"
                                title="수정"
                                onClick={() => ShopFormUtils.handleShopEdit(navigate, shop.code, 0, 20)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                className="text-gray-400 hover:text-red-600"
                                title="삭제"
                                onClick={() => ShopFormUtils.handleShopDelete(shop.code)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BrandList;